import axios from 'axios';
import { AuthContextProps, useAuth } from 'oidc-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { clearOrderDrafts } from 'src/adminPanelV2/panels/office/kunden/bestellung/orderDraftStore';
import { clearSearchMemory } from 'src/adminPanelV2/panels/office/kunden/kundenSucheState';
import { isAdminContext } from 'src/api';
import { ROUTE_ADMIN, ROUTE_HOME } from 'src/config/routes';
import { AppRoute } from 'src/core/AppRoute';
import { resetShoppingCart } from 'src/store/cart/actions';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import { clearMyOffers, clearParkedCart } from 'src/store/offer/action';

export type AccountRole =
    | 'user'
    | 'POTENTIAL_PARTNER'
    | 'Partner'
    | 'POTENTIAL_LICENSEE'
    | 'POTENTIAL_OP'
    | 'LICENSEE'
    | 'POTENTIAL_MLG'
    | 'MLG'
    | 'REFERRAL_PARTNER'
    | 'OFFICE_PROFI'
    | 'LANDING_PAGE'
    | 'TRANSLATOR'
    | 'VENDING_ADMIN'
    | 'VENDING_CUSTOMER'
    | 'VENDING_VIEWER'
    /* Admin */
    | 'uma_authorization'
    | 'offline_access'
    | 'Service'
    | 'IT'
    | 'Verrechnung'
    | 'FrontOffice'
    | 'ADMIN'
    | 'DIRECT_SELLING'
    | 'Logistikadministrator'
    | 'Logistikmitarbeiter';
export type AccountPrivilege =
    | 'potential_partner'
    | 'partner'
    | 'potential_licensee'
    | 'potential_op'
    | 'licensee'
    | 'potential_mlg'
    | 'mlg'
    | 'referral_partner'
    | 'office_profi'
    | 'landing_page'
    | 'vending_admin'
    | 'vending_customer'
    | 'vending_viewer'
    | 'customer'
    | 'translator'
    /* Admin */
    | 'Service'
    | 'IT'
    | 'Verrechnung'
    | 'FrontOffice'
    | 'ADMIN'
    | 'DIRECT_SELLING'
    | 'Logistikadministrator'
    | 'Logistikmitarbeiter';

export interface Account {
    signIn: (redirectUri?: string | null) => void;
    signOut: () => void;
    hasPrivilege: (privilege: AccountPrivilege | AccountPrivilege[]) => boolean;
    /** Every realm role of the ID token, incl. the AdminPanel page roles (AP_*) of an employee area. */
    realmRoles: readonly string[];
    enforcePrivilege: (route: AppRoute) => void;
    isLoggedIn: boolean;
    auth: AuthContextProps | null;
}

export function useAccount(): Account {
    const auth = useAuth();
    const history = useHistory();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Memoized login status and roles
    const [isLoggedIn, roles] = useMemo(
        () => [
            auth !== null && auth.userData != null,
            (auth.userData?.profile?.realm_access as { roles: AccountRole[] | undefined } | undefined)?.roles ?? [],
        ],
        [auth]
    );

    // Set the Authorization Header for axios depending on login state
    useEffect(() => {
        const tokenType = auth.userData?.token_type;
        const accessToken = auth.userData?.access_token;

        let authorizationHeader;

        if (isLoggedIn && tokenType && accessToken) {
            authorizationHeader = `${tokenType} ${accessToken}`;
        }

        axios.defaults.headers.common.Authorization = authorizationHeader;
    }, [isLoggedIn, auth]);

    // Memoized sign in function
    const signIn = useCallback(
        (redirectUri?: string | null) => {
            auth?.signIn({
                redirect_uri: redirectUri == null ? undefined : redirectUri,
            });
        },
        [auth]
    );

    // Memoized sign out function — the cart is dropped locally, a calc would still carry the old token
    const signOut = useCallback(() => {
        dispatch(clearMyOffers());
        dispatch(clearParkedCart());
        dispatch(resetShoppingCart());
        clearSearchMemory();
        clearOrderDrafts(auth?.userData?.profile?.sub ?? '');
        auth?.signOutRedirect();
    }, [auth, dispatch]);

    // Memoized privilige check function
    const hasSinglePrivilege = useCallback(
        (privilege: AccountPrivilege) => {
            switch (privilege) {
                case 'potential_licensee':
                    return roles.includes('user') && roles.includes('POTENTIAL_LICENSEE');
                case 'licensee':
                    return roles.includes('user') && roles.includes('LICENSEE');
                case 'potential_partner':
                    return roles.includes('user') && roles.includes('POTENTIAL_PARTNER');
                case 'partner':
                    return roles.includes('user') && roles.includes('Partner');
                case 'potential_mlg':
                    return roles.includes('user') && roles.includes('POTENTIAL_MLG');
                case 'mlg':
                    return roles.includes('user') && roles.includes('MLG');
                case 'referral_partner':
                    return roles.includes('user') && roles.includes('REFERRAL_PARTNER');
                case 'potential_op':
                    return roles.includes('user') && roles.includes('POTENTIAL_OP');
                case 'office_profi':
                    return roles.includes('user') && roles.includes('OFFICE_PROFI');
                case 'landing_page':
                    return roles.includes('user') && roles.includes('LANDING_PAGE');
                case 'translator':
                    return roles.includes('user') && roles.includes('TRANSLATOR');
                case 'vending_admin':
                    return roles.includes('user') && roles.includes('VENDING_ADMIN');
                case 'vending_customer':
                    return roles.includes('user') && roles.includes('VENDING_CUSTOMER');
                case 'vending_viewer':
                    return roles.includes('user') && roles.includes('VENDING_VIEWER');
                case 'Service':
                    return roles.includes('user') && roles.includes('Service');
                case 'IT':
                    return roles.includes('user') && roles.includes('IT');
                case 'Verrechnung':
                    return roles.includes('user') && roles.includes('Verrechnung');
                case 'FrontOffice':
                    return roles.includes('user') && roles.includes('FrontOffice');
                case 'ADMIN':
                    return roles.includes('user') && roles.includes('ADMIN');
                case 'DIRECT_SELLING':
                    return roles.includes('user') && roles.includes('DIRECT_SELLING');
                case 'Logistikadministrator':
                    return roles.includes('user') && roles.includes('Logistikadministrator');
                case 'Logistikmitarbeiter':
                    return roles.includes('user') && roles.includes('Logistikmitarbeiter');
                default:
                    return false;
            }
        },
        [roles]
    );

    const hasPrivilege = useCallback(
        (privilages: AccountPrivilege | AccountPrivilege[]) => [privilages].flat().some(hasSinglePrivilege),
        [hasSinglePrivilege]
    );

    const enforcePrivilege = useCallback(
        appRoute => {
            // Do nothing if route has no privilege assigned
            if (appRoute.accessPrivilege === undefined) return;
            // Do nothing if current user has the correct privilege
            if (hasPrivilege(appRoute.accessPrivilege)) return;

            if (
                history.location.pathname.includes(ROUTE_ADMIN.path) &&
                !(isAdminContext() && isLoggedIn && hasPrivilege('ADMIN'))
            ) {
                dispatch(
                    enqueueSnackbarAction({
                        message: t('routes:adminpanel.noPermission'),
                        options: {
                            key: new Date().getTime() + Math.random(),
                            variant: 'error',
                        },
                    })
                );
            }
            // Replace route with 401
            history.replace(ROUTE_HOME.path);
        },
        [hasPrivilege, history]
    );

    return {
        signIn,
        signOut,
        hasPrivilege,
        realmRoles: roles,
        enforcePrivilege,
        isLoggedIn,
        auth,
    };
}
